import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_RECEIPT_ITEMS,
  REVENUECAT_RECEIPT_OVERSEAS_ROW,
  REVENUECAT_RECEIPT_ROW,
  SECURITY,
  admobOverseasRow,
  admobRow,
  changes,
  rights,
  table,
} from './common'

/**
 * 광고 + 광고 제거 인앱결제를 붙인 게임의 공통 방침 골격 (2026-08-24).
 *
 * **이 골격은 `game-offline.ts` 를 대신한다.** 오마주 3종(Isle Ink·Tucked·Tidecipher)에
 * 광고와 결제가 들어가면서 "수집 0 · 네트워크 없음"이 그 자리에서 거짓이 됐다
 * (docs/privacy-policy.md §절대 규칙 2). 조항만 더하지 않고 **거짓이 된 문장을 같이
 * 걷어낸 것**이 이 파일이다 — 무권한·무전송·광고 SDK 없음 문장이 전부 사라졌다.
 *
 * 세 게임이 파일 하나를 쓰는 이유는 **지금 사실이 세 앱에서 글자 그대로 같아서다**:
 *   · 광고 = **플레이 중 하단 배너 상시 + 게임오버 전면 1회** (2026-08-25 사용자 지시 —
 *     docs/rules.md §광고. 배너는 같은 날 아침에 내렸다가 "자리를 미리 확보하고 그리라"는
 *     조건과 함께 되살렸다. 보상형은 파사드에 API 만 있고 안 쓴다)
 *   · 결제 = 광고 제거 비소모성 1종, 영수증만 RevenueCat 에 REST 로 전송
 *   · 저장 = 기기 안 SaveStore 하나, 계정·운영자 서버 없음
 *   · 분석·오류 수집 SDK 없음, 앱 안 문의·후원 화면 없음
 * 사실이 갈라지면 파일도 가른다.
 *
 * 배선 원본: `game/shared/addons/cottonray/{ads,admob_adapter,iap,revenuecat}.gd`.
 * 동의를 다시 고르는 “Privacy options” 진입점은 `settings_menu.gd` 가 `Ads.has_privacy_options()` 로 다는 줄이다 —
 * **그 줄을 떼면 아래 자동수집 조항의 거부 경로 하나가 그 자리에서 거짓이 된다.**
 * **보상형 광고·문의·후원·Crashlytics 를 붙이는 날 이 파일을 같은 커밋에서 고친다.**
 */

/**
 * 광고 배치 — 위탁·국외이전 표의 "무슨 광고인가" 칸. 여덟 게임이 같다.
 * **여기 문장이 코드보다 넓으면 방침이 거짓이 된다.** 지금 코드는 배너 상시 + 게임오버 전면이다
 * (`game/shared/addons/cottonray/ads.gd`).
 */
const AD_PURPOSE = 'Serving a banner ad during play and one interstitial ad when a game ends'

export function makeAdSupportedGamePolicy(input: {
  slug: string
  title: string
  /** 스토어 표기 이름 (영어) */
  name: string
  /** 게임이 기기에 저장하는 것 — 사실 그대로, 한 문장으로 */
  storedOnDevice: string
  /** 광고 제거 상품 ID (`com.cottonray.<게임키>.remove_ads` — 오마주 3종은 신규 앱이라 `com.cottonray` 다) */
  removeAdsProductId: string
  effectiveDate: string
  /** 시행일과 다를 수 있다. 생략하면 시행일과 같다 */
  lastRevisedDate?: string
  history: readonly string[]
}): PolicyDocument {
  const SECTIONS: readonly PolicySection[] = [
    {
      heading: 'Article 1. Purposes of processing personal data',
      paragraphs: [
        'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
      ],
      bullets: [
        'Serving advertising — showing a banner ad in a strip along the bottom of the screen while you play, and a single interstitial ad when a game ends, which is how a free game is funded. The game reserves that strip before it draws itself, so the banner never covers what you are playing. Ads are served by a processor (Google AdMob), and the Operator receives none of your information',
        'Handling the ad-removal purchase — confirming that the one-off “remove ads” purchase actually took place, so that it can be restored later and a duplicate charge or a refund can be looked into',
      ],
      note: 'The game itself is played entirely on your device. Nothing in the two purposes above involves what you do in a game.',
    },
    {
      heading: 'Article 2. How the game is built — play stays on your device',
      paragraphs: [
        `${input.name} works without an account. ${input.storedOnDevice} All of it is stored and processed on your device alone; none of it is transmitted to the Operator, who runs no server for this game.`,
        `Two things do leave your device, and neither is anything you did in a game. The first is what an advertising request carries, which goes from your device straight to Google. The second happens only if you buy the ad removal (${input.removeAdsProductId}): the store carries out the payment, and the app then sends the receipt for that purchase to RevenueCat so that it can be verified and counted.`,
        'The game contains no analytics SDK and no crash-reporting SDK, has no login, no in-app support screen, and no sponsorship screen, and it never sends your saved game, your board, or your scores to anyone.',
      ],
    },
    {
      heading: 'Article 3. Personal data processed',
      paragraphs: [
        'The items below are everything processed for this game. Neither of the two collected items reaches the Operator as data about you — advertising data goes from your device directly to Google, and the purchase record is kept by the payment processor named in Article 7.',
      ],
      bullets: [
        `[Not collected] ${input.storedOnDevice} These stay on your device only and are not personal data held by the Operator`,
        '[Collected automatically, from the moment an ad is requested] Device and network information and your advertising identifier (ADID on Android, IDFA on iOS), together with coarse IP-based location — processed by Google (AdMob), which supplies the ads, for ad delivery, measurement, frequency capping, and fraud prevention. The Operator does not receive this information',
        `[Collected automatically, and only if you buy the ad removal] ${REVENUECAT_RECEIPT_ITEMS}`,
      ],
      note: 'No account, name, email address, phone number, precise location, contacts, photos, or address book is collected. No card number, cardholder name, or billing address ever reaches the app or the Operator — the store handles the payment. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected, and no analytics or crash diagnostics are sent.',
    },
    {
      heading: 'Article 4. Use of device permissions',
      paragraphs: [
        'The game asks you for no permission at runtime — no camera, photos, location, contacts, or notifications. It does declare the permissions below, which the operating system grants when the app is installed.',
      ],
      bullets: [
        'Network access (INTERNET) — used to fetch ads and to complete a purchase. The game did not need it before advertising was introduced',
        'Billing (com.android.vending.BILLING, Android) — used to offer the ad-removal purchase through the store',
        'Advertising identifier (com.google.android.gms.permission.AD_ID, Android) — added to the app by the Google Mobile Ads SDK so that the advertising identifier can be read for ad delivery',
      ],
    },
    {
      heading: 'Article 5. Processing and retention periods',
      table: table(RETENTION_HEADERS, [
        [
          'Game progress, records, and app settings',
          'On your device only (not held by the Operator)',
          'Not applicable',
        ],
        [
          'Device information and advertising identifier used in serving ads',
          'Subject to Google’s retention policy',
          'Your consent (handled by the processor)',
        ],
        [
          'Ad-removal purchase record',
          'Kept while the game is served; destroyed when the Operator closes the game’s payment project, or on your request',
          'Performance of the purchase contract (Article 15(1)4 of the Personal Information Protection Act)',
        ],
      ]),
      note: 'What is on your device disappears when you delete the game, or reset its data in system settings, and the Operator holds no separate copy of it. The payment itself is carried out by the store, which keeps its own transaction records under the legislation that applies to it; what the Operator can see is only the anonymous purchase record above, which carries no name, email address, or card detail.',
    },
    {
      heading: 'Article 6. Provision of personal data to third parties',
      paragraphs: [
        'The Operator provides no personal data to third parties. What Google and RevenueCat receive is entrusted processing under Article 7, not provision to a third party. Personal data is never sold.',
      ],
    },
    {
      heading: 'Article 7. Entrustment of personal data processing',
      paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
      table: table(PROCESSOR_HEADERS, [admobRow(AD_PURPOSE), [...REVENUECAT_RECEIPT_ROW]]),
      note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. The payment itself is carried out by the store, which acts as the payment provider; your card number, name, and address are handled there and never reach the app or the Operator. If the processors change, this policy is updated and notice given in advance.',
    },
    {
      heading: 'Article 8. Transfer of personal data abroad',
      paragraphs: [
        'The processors named in Article 7 are located outside Korea, and the entrustment transfers personal data abroad as follows.',
      ],
      table: table(OVERSEAS_HEADERS, [
        admobOverseasRow(AD_PURPOSE),
        [...REVENUECAT_RECEIPT_OVERSEAS_ROW],
      ]),
      note: `${OVERSEAS_REFUSAL} For advertising there is a further way to stop the transfer inside the game itself: buying the ad removal stops every ad, after which the game makes no advertising request at all and nothing goes to Google. If you never buy anything, no purchase record is created and nothing is transferred to RevenueCat. Deleting the game stops both transfers.`,
    },
    DISPOSAL,
    rights(
      [
        'In the game — progress and settings can be reset from the in-game settings, and deleting the game removes everything that was on your device',
        'Removing ads — buying the ad removal stops the banner and the interstitial, after which the game sends nothing to Google and gives the strip back to the game',
        'Advertising identifier — resetting or deleting it in device settings breaks the link between you and any identifier already sent',
      ],
      'the ad-removal purchase record held by the payment processor — the Operator holds nothing else from this game',
    ),
    {
      heading:
        'Installation and operation of automatic collection devices, and how to refuse them (including advertising)',
      paragraphs: [
        'The game shows a banner ad along the bottom of the screen while you play and one interstitial ad when a game ends, both served by Google LLC’s AdMob. The strip the banner sits in is left empty by the game before it draws, so the banner sits beside the game rather than over it. Before any ad is requested, the game runs Google’s User Messaging Platform consent flow. In a region where a consent form is required — the European Economic Area and the United Kingdom among them — that form is shown before the first ad, and the answer you give in it decides whether the ads you then see are personalized or non-personalized. In those regions the game also keeps a way back to that form: a “Privacy options” item in the in-game settings, which opens the same form again so that the answer can be changed later. Where no form is required, the Google Mobile Ads SDK is initialised without one, and no such item appears.',
        'Whichever of those applies, Google may process device and network information, an advertising identifier (ADID on Android, IDFA on iOS), and coarse IP-based location for ad delivery, measurement, frequency capping, and fraud prevention. That information goes from your device directly to Google without passing through the Operator, and Google’s handling of it is governed by the Google Privacy Policy (https://policies.google.com/privacy).',
        'You can refuse this collection. Where the consent form was shown, open “Privacy options” in the in-game settings: the same form opens again and the answer you gave earlier can be changed, including withdrawing it. A changed answer applies to every ad the game requests from then on, and the ads it had already fetched under the earlier answer are discarded when the form closes. You can also reset or delete your advertising identifier, or turn off personalized advertising, in device settings — Android: Settings → Privacy → Ads; iOS: Settings → Privacy & Security → Apple Advertising. Refusing that way leaves every feature of the game available to you, and ads may still appear as non-personalized. To stop advertising entirely, buy the ad removal: no ad is shown afterwards and the game makes no request to Google at all. Deleting the game also ends the collection.',
      ],
      note: 'Apart from what serving those ads requires, the Operator uses no cookie, web beacon, or other automatic collection device, and does not itself use any collected data for ad targeting or profiling. The only purchase the game offers is the one-off ad removal; it unlocks no other content, the payment is carried out by the store, and the Operator processes only the purchase record described in Articles 3, 5, 7, and 8.',
    },
    CHILDREN,
    SECURITY,
    OFFICER,
    REMEDIES,
    changes(input.effectiveDate, input.history),
  ]

  return {
    slug: input.slug,
    title: input.title,
    intro: `${OPERATOR}, establishes and publishes the following privacy policy for ${input.name} (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
    effectiveDate: input.effectiveDate,
    lastRevisedDate: input.lastRevisedDate ?? input.effectiveDate,
    sections: SECTIONS,
  }
}
